import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { AdminStatCardFlex } from './adminUi'

function roleClass(role) {
  const r = (role || '').toLowerCase()
  if (r === 'serviceprovider') return 'service'
  return r
}

export default function AdminSuspended() {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [roleFilter, setRoleFilter] = useState('all')
  const [reasonFilter, setReasonFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [detailUser, setDetailUser] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = () => {
    axios.get('/api/admin/suspended-users', { params: { role: roleFilter, reason: reasonFilter, search } })
      .then((r) => {
        setStats(r.data.stats)
        setRows(r.data.users || [])
      })
      .catch(() => toast.error('\u52a0\u8f7d\u5931\u8d25'))
  }

  useEffect(() => { load() }, [roleFilter, reasonFilter, search])

  const unsuspend = async (user) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/users/${user.id}/unsuspend`)
      toast.success('User unsuspended')
      setDetailUser(null)
      load()
    } catch {
      toast.error('Failed to unsuspend user')
    } finally {
      setBusy(false)
    }
  }

  const filtered = useMemo(() => rows, [rows])

  return (
    <AdminLayout dangerTopbar>
      <div className="admin-stats-row cols-4">
        <AdminStatCardFlex icon="moderation" cls="red" label="Total Suspended" value={stats?.total ?? 0} sub={`${stats?.pctOfAll ?? 0}% of all users`} />
        <AdminStatCardFlex icon="alertTriangle" cls="orange" label="Terms Violation" value={stats?.termsViolation ?? 0} sub="Most common reason" />
        <AdminStatCardFlex icon="search" cls="blue" label="Under Review" value={stats?.underReview ?? 0} sub="Pending investigation" />
        <AdminStatCardFlex icon="check" cls="green" label="Unsuspended (30d)" value={stats?.unsuspended30d ?? 0} sub="Restored accounts" />
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Suspended Accounts</span>
        </div>
        <div className="admin-filter-row">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="GP">GP</option>
            <option value="LP">LP</option>
            <option value="Startup">Startup</option>
            <option value="Service">Service</option>
          </select>
          <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
            <option value="all">All Reasons</option>
            <option value="terms">Terms Violation</option>
            <option value="fraud">Suspected Fraud</option>
            <option value="inactive">Inactivity</option>
            <option value="other">Other</option>
          </select>
          <input type="text" placeholder="Search by name, email or company..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <button type="button" className="admin-btn sm secondary" onClick={() => { setRoleFilter('all'); setReasonFilter('all'); setSearch('') }}>Reset</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Company</th>
              <th>Role</th>
              <th>Suspended On</th>
              <th>Reason</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ fontWeight: 650 }}>{u.system_name?.split('-').slice(1, 2).join(' ') || u.system_name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--admin-text-muted)' }}>{u.work_email}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--admin-text-muted)', marginTop: 2 }}>
                    Suspended {u.updated_at ? new Date(u.updated_at).toLocaleDateString() : '-'}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 12.5 }}>{u.company_name || '-'}</div>
                  <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{u.company_type || u.role} · {u.hq_region || 'APAC'}</div>
                </td>
                <td><span className={`admin-role-badge ${roleClass(u.role)}`}>{u.role === 'ServiceProvider' ? 'Service' : u.role}</span></td>
                <td style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{u.updated_at ? new Date(u.updated_at).toLocaleDateString() : '-'}</td>
                <td><span className={`admin-reason-tag ${u.reason_key || 'other'}`}>{u.reason_label || 'Other'}</span></td>
                <td className="admin-td-actions">
                  <div className="admin-action-btns">
                  <button type="button" className="admin-abtn admin-abtn-view" onClick={() => setDetailUser(u)}>View</button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-success admin-btn-sm"
                    disabled={busy}
                    onClick={() => unsuspend(u)}
                  >
                    Unsuspend
                  </button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#909399' }}>No suspended users. Run npm run db:seed:admin</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminModal
        open={!!detailUser}
        title="Suspended User"
        onClose={() => setDetailUser(null)}
        footer={(
          <>
            <button type="button" className="admin-btn secondary" onClick={() => setDetailUser(null)}>Close</button>
            <button type="button" className="admin-btn admin-btn-success" disabled={busy} onClick={() => unsuspend(detailUser)}>
              Unsuspend
            </button>
          </>
        )}
      >
        {detailUser && (
          <div className="admin-review-meta-grid">
            <div className="admin-review-meta-block">
              <p><strong>Name:</strong> {detailUser.system_name}</p>
              <p><strong>Email:</strong> {detailUser.work_email}</p>
              <p><strong>Role:</strong> {detailUser.role === 'ServiceProvider' ? 'Service' : detailUser.role}</p>
            </div>
            <div className="admin-review-meta-block">
              <p><strong>Company:</strong> {detailUser.company_name || '—'}</p>
              <p><strong>Region:</strong> {detailUser.hq_region || '—'}</p>
              <p><strong>Reason:</strong> {detailUser.reason_label || 'Other'}</p>
            </div>
            <div className="admin-review-meta-block" style={{ gridColumn: '1 / -1' }}>
              <p><strong>Notes:</strong> {detailUser.registration_notes || '—'}</p>
              <p><strong>Suspended:</strong> {detailUser.updated_at ? new Date(detailUser.updated_at).toLocaleString() : '—'}</p>
            </div>
          </div>
        )}
      </AdminModal>
    </AdminLayout>
  )
}
